import type { Observation } from '@/types';

const DEFAULT_AUTHOR_REPUTATION = 0.45;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function toFinite(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getGeoScore(status?: Observation['geoStatus']): number {
  switch (status) {
    case 'PROCHE_MATCH':
      return 1;
    case 'INCERTAIN':
      return 0.5;
    case 'NON_VERIFIE':
      return 0.4;
    case 'INDISPONIBLE':
      return 0.3;
    case 'HORS_ZONE':
      return 0.1;
    default:
      return 0.4;
  }
}

function getHygieneScore(observation: Observation): number {
  const hasIdentity =
    Boolean(observation.nom?.trim()) &&
    Boolean(observation.prenom?.trim()) &&
    Boolean(observation.playerNumber?.trim()) &&
    Boolean(observation.poste?.trim());
  const wordCount = toFinite(observation.qualityWordCount, 0);
  return hasIdentity && wordCount >= 20 ? 1 : 0.5;
}

export function computeObservationTrust(
  observation: Observation,
  authorReputation = DEFAULT_AUTHOR_REPUTATION
): number {
  const quality = clamp(toFinite(observation.qualityScore, 40) / 100, 0, 1);
  const geo = getGeoScore(observation.geoStatus);
  const reputation = clamp(authorReputation, 0, 1);
  const hygiene = getHygieneScore(observation);
  const trust = 0.45 * quality + 0.2 * geo + 0.2 * reputation + 0.15 * hygiene;
  return clamp(trust, 0.05, 1);
}

function effectiveSampleSize(weights: number[]): number {
  const sum = weights.reduce((acc, value) => acc + value, 0);
  const sumSquares = weights.reduce((acc, value) => acc + value * value, 0);
  if (sum <= 0 || sumSquares <= 0) return 0;
  return (sum * sum) / sumSquares;
}

export interface AggregatedObservationScore {
  robustRating: number;
  confidenceScore: number;
  confidenceLevel: 'FAIBLE' | 'MOYENNE' | 'ELEVEE';
  isProvisional: boolean;
  effectiveSampleSize: number;
}

export function aggregateObservationScores(observations: Observation[]): AggregatedObservationScore {
  if (!observations.length) {
    return {
      robustRating: 0,
      confidenceScore: 0,
      confidenceLevel: 'FAIBLE',
      isProvisional: true,
      effectiveSampleSize: 0,
    };
  }

  const rated = observations
    .map((obs) => ({
      rating: clamp(toFinite(obs.rating, 0), 0, 10),
      trust: computeObservationTrust(obs),
    }))
    .filter((item) => Number.isFinite(item.rating) && item.trust > 0);

  if (!rated.length) {
    return {
      robustRating: 0,
      confidenceScore: 0,
      confidenceLevel: 'FAIBLE',
      isProvisional: true,
      effectiveSampleSize: 0,
    };
  }

  const weights = rated.map((item) => item.trust);
  const weightedSum = rated.reduce((acc, item) => acc + item.rating * item.trust, 0);
  const weightTotal = weights.reduce((acc, item) => acc + item, 0);
  const mean = weightTotal > 0 ? weightedSum / weightTotal : 0;
  const variance =
    weightTotal > 0
      ? rated.reduce((acc, item) => acc + item.trust * (item.rating - mean) ** 2, 0) / weightTotal
      : 0;
  const sd = Math.sqrt(Math.max(0, variance));
  const nEff = effectiveSampleSize(weights);
  const robustRating = clamp(mean - 0.15 * sd - 1.2 / Math.sqrt(Math.max(1, nEff)), 0, 10);
  const avgTrust = weightTotal / rated.length;
  const confidenceScore = clamp(100 * (0.6 * avgTrust + 0.4 * Math.min(1, nEff / 8)), 0, 100);
  const confidenceLevel: 'FAIBLE' | 'MOYENNE' | 'ELEVEE' =
    confidenceScore >= 75 ? 'ELEVEE' : confidenceScore >= 45 ? 'MOYENNE' : 'FAIBLE';
  const isProvisional = nEff < 3 || confidenceScore < 45;

  return {
    robustRating,
    confidenceScore,
    confidenceLevel,
    isProvisional,
    effectiveSampleSize: nEff,
  };
}

export function enrichObservationWithTrust(observation: Observation): Observation {
  return {
    ...observation,
    trustScore: computeObservationTrust(observation),
  };
}
